import { Box } from '@chakra-ui/react';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import {
  MODULE_KEYS_METRIC_CARDS_DEER,
  REVIEWS_METRIC_CARDS,
  REVIEW_CALIBRATION_METRIC_CARD_TYPES,
} from 'Deer/metricCards/constants';
import SkeletonLoader from 'components/SkeletonLoader';

import { useGetReviewsManageTabularDataQueryV1 } from '../../dataHooks';
import { REVIEW_MANAGE_GRID_COLUMN_ID } from '../ReviewManageAGGrid/reviewMangeGridConstants';

// eslint-disable-next-line consistent-return
export const getCardIdFromStatType = (type: keyof typeof REVIEW_CALIBRATION_METRIC_CARD_TYPES) => {
  // eslint-disable-next-line default-case
  switch (type) {
    case REVIEW_CALIBRATION_METRIC_CARD_TYPES.IDENTIFICATION:
      return REVIEWS_METRIC_CARDS?.identifiedForCalibration;
    case REVIEW_CALIBRATION_METRIC_CARD_TYPES.CALIBRATION_STATUS:
      return REVIEWS_METRIC_CARDS.pendingCalibration;
  }
};

export const getStatTitle = (type: keyof typeof REVIEW_CALIBRATION_METRIC_CARD_TYPES) => {
  switch (type) {
    case REVIEW_CALIBRATION_METRIC_CARD_TYPES.IDENTIFICATION:
      return 'Identified for Calibration';
    case REVIEW_CALIBRATION_METRIC_CARD_TYPES.CALIBRATION_STATUS:
      return 'Employees pending calibration';
    default:
      return '';
  }
};

type ReviewCalibrateMetricsProps = {
  gridStatePersistenceKey: string;
};

export const ReviewCalibrateMetrics = ({ gridStatePersistenceKey }: ReviewCalibrateMetricsProps) => {
  const { isFetching, data: reviewManageGridData } = useGetReviewsManageTabularDataQueryV1();

  const reviewManageGridRowData = reviewManageGridData?.rows || [];

  const reviewManageGridColumnData = reviewManageGridData?.columns || [];

  const identifiedForCalibrationColumnVisible = Boolean(
    (reviewManageGridColumnData || []).find(
      (col) => col.columnId === REVIEW_MANAGE_GRID_COLUMN_ID.IDENTIFIED_FOR_CALIBRATION
    )
  );

  const pendingCalibration = reviewManageGridRowData.filter(
    (row) => row.CALIBRATION_STATUS && row.CALIBRATION_STATUS === 'PENDING'
  ).length;

  const identifiedForCalibration = reviewManageGridRowData.filter(
    (row) => row.IDENTIFIED_FOR_CALIBRATION && row.IDENTIFIED_FOR_CALIBRATION === 'Yes'
  ).length;

  const totalCalibration = reviewManageGridRowData.length;
  const cardKPIStats = {
    CALIBRATION_STATUS: { numerator: pendingCalibration, denominator: totalCalibration },
    IDENTIFIED_FOR_CALIBRATION: { numerator: identifiedForCalibration, denominator: totalCalibration },
  };

  if (identifiedForCalibrationColumnVisible === false) {
    delete cardKPIStats.IDENTIFIED_FOR_CALIBRATION;
  }

  if (isFetching) {
    return <SkeletonLoader />;
  }

  const cardKPIData = Object.keys(cardKPIStats ?? []).map((type) => ({
    cardId: getCardIdFromStatType(type as keyof typeof REVIEW_CALIBRATION_METRIC_CARD_TYPES),
    title: getStatTitle(type as keyof typeof REVIEW_CALIBRATION_METRIC_CARD_TYPES),
    count: cardKPIStats?.[type]?.numerator || '0',
    subTitle: ![undefined, null].includes(cardKPIStats?.[type]?.averageRating)
      ? `Average score : ${cardKPIStats?.[type]?.averageRating}`
      : '',
    totalCount: cardKPIStats?.[type]?.denominator,
    isAICard: type === REVIEW_CALIBRATION_METRIC_CARD_TYPES.IDENTIFICATION,
    isBeta: type === REVIEW_CALIBRATION_METRIC_CARD_TYPES.IDENTIFICATION,
  }));

  if (!cardKPIData?.length) {
    return null;
  }

  // sort the cards in order
  const orderedList = [
    REVIEWS_METRIC_CARDS.identifiedForCalibration,
    REVIEWS_METRIC_CARDS.noRatingChange,
    REVIEWS_METRIC_CARDS.pendingCalibration,
  ];
  cardKPIData.sort((a, b) => {
    const indexA = orderedList.indexOf(a.cardId);
    const indexB = orderedList.indexOf(b.cardId);

    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });

  return (
    <Box mb={3}>
      <MetricCards
        height="100%"
        columns={`repeat(${Math.max(cardKPIData?.length, 3)}, 1fr)`}
        cardTitle="Metrics"
        cards={cardKPIData}
        module={MODULE_KEYS_METRIC_CARDS_DEER.REVIEW}
        gridStatePersistenceKey={gridStatePersistenceKey}
        cardStyle="style3"
      />
    </Box>
  );
};
